import { useEffect, useState } from 'react'
import { useTheme } from '../contexts/ThemeContext'

interface CheckInTimeModalProps {
  isOpen: boolean
  onClose: () => void
  checkInTime?: Date // Thời gian lúc bấm điểm danh
}

export default function CheckInTimeModal({ isOpen, onClose, checkInTime }: CheckInTimeModalProps) {
  const { theme } = useTheme()
  const [currentDateTime, setCurrentDateTime] = useState<string>('')
  const [currentTime, setCurrentTime] = useState<string>('')

  useEffect(() => {
    if (isOpen && checkInTime) {
      // Chỉ lấy thời gian lúc bấm điểm danh, không cập nhật real-time
      const now = checkInTime
      
      // Format date: "Thứ Hai, 29 tháng 12 2025"
      const days = ['Chủ Nhật', 'Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy']
      const months = ['tháng 1', 'tháng 2', 'tháng 3', 'tháng 4', 'tháng 5', 'tháng 6', 
                     'tháng 7', 'tháng 8', 'tháng 9', 'tháng 10', 'tháng 11', 'tháng 12']
      
      const dayName = days[now.getDay()]
      const day = now.getDate()
      const month = months[now.getMonth()]
      const year = now.getFullYear()
      
      const dateString = `${dayName}, ${day} ${month} ${year}`
      
      // Format time: "HH:MM:SS"
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      const seconds = String(now.getSeconds()).padStart(2, '0')
      
      const timeString = `${hours}:${minutes}:${seconds}`
      
      setCurrentDateTime(dateString)
      setCurrentTime(timeString)
    }
  }, [isOpen, checkInTime])

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 ${theme === 'dark' ? 'bg-black/80' : 'bg-gray-900/80'} backdrop-blur-sm z-50`}
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className={`relative w-full max-w-md rounded-2xl border-2 ${theme === 'dark' ? 'border-gold-500 bg-gradient-to-br from-gold-900/30 via-black/90 to-black' : 'border-gold-400 bg-white/95'} shadow-2xl overflow-hidden`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Decorative Background */}
          <div className={`absolute top-0 right-0 w-40 h-40 ${theme === 'dark' ? 'bg-gold-500/10' : 'bg-gold-400/10'} rounded-full -mr-20 -mt-20 blur-3xl`}></div>
          <div className={`absolute bottom-0 left-0 w-40 h-40 ${theme === 'dark' ? 'bg-gold-500/10' : 'bg-gold-400/10'} rounded-full -ml-20 -mb-20 blur-3xl`}></div>

          {/* Close Button */}
          <button
            onClick={(e) => {
              e.stopPropagation()
              onClose()
            }}
            className={`absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-lg transition z-10 cursor-pointer ${theme === 'dark' ? 'text-gold-400 hover:bg-gold-900/30 hover:text-white' : 'text-gray-700 hover:bg-gray-200 hover:text-gray-900'}`}
            aria-label="Close"
            type="button"
          >
            <i className="pi pi-times text-xl font-bold"></i>
          </button>

          <div className="relative p-8 text-center">
            {/* Icon */}
            <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${theme === 'dark' ? 'bg-gold-500/20' : 'bg-gold-400/20'}`}>
              <i className={`pi pi-calendar text-4xl ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}></i>
            </div>

            {/* Title */}
            <h3 className={`text-2xl md:text-3xl font-bold mb-6 ${theme === 'dark' ? 'text-gold-400' : 'text-gray-900'}`}>
              Thời Gian Điểm Danh
            </h3>

            {/* Date Display */}
            <div className={`mb-6 p-4 rounded-xl ${theme === 'dark' ? 'bg-gold-500/10 border border-gold-500/30' : 'bg-gold-400/10 border border-gold-400/30'}`}>
              <div className={`text-sm md:text-base font-medium mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Ngày
              </div>
              <div className={`text-lg md:text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {currentDateTime}
              </div>
            </div>

            {/* Time Display */}
            <div className={`mb-6 p-4 rounded-xl ${theme === 'dark' ? 'bg-gold-500/10 border border-gold-500/30' : 'bg-gold-400/10 border border-gold-400/30'}`}>
              <div className={`text-sm md:text-base font-medium mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Giờ
              </div>
              <div className={`text-3xl md:text-4xl font-bold font-mono ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}>
                {currentTime}
              </div>
            </div>

            {/* Status Message */}
            <div className={`text-sm md:text-base ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-6 flex items-center justify-center gap-2`}>
              <i className={`pi pi-check-circle ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}></i>
              <span>Đã ghi nhận thời gian điểm danh</span>
            </div>

            {/* Close Button */}
            <button
              onClick={onClose}
              className={`w-full py-3 px-6 bg-gold-gradient text-black font-bold text-base rounded-lg hover:shadow-lg hover:shadow-gold-500/50 transition transform hover:scale-105`}
            >
              Đóng
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

